import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import ProposalPortalPage from './views/proposals/ProposalPortalPage';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PrivateRoute from 'services/routes/PrivateRoute';
import PublicRoute from 'services/routes/PublicRoute';
import ProductList from './views/products/ProductList';
import ProductDetail from './views/products/ProductDetail';
import ServiceList from './views/products/ServiceList';
import ProductCategoryList from './views/products/ProductCategoryList';
import SettingsMenu from './components/settings/SettingsMenu';
import LoginPage from './views/account/LoginPage';
import TwoFactorPage from './views/account/TwoFactorPage';
import TwoFactorSetupPage from './views/account/TwoFactorSetupPage';
import ForgotPasswordPage from './views/account/ForgotPasswordPage';
import ResetPasswordPage from './views/account/ResetPasswordPage';
import SetPasswordPage from './views/account/SetPasswordPage';
import PayInvoicePage from './views/account/PayInvoicePage';
import CreateTrialPage from './views/account/CreateTrialPage';
import SettingsPage from './components/settings/SettingsPage';
import OrganizationDetail from './views/contacts/OrganizationDetail';
import PersonDetail from './views/contacts/PersonDetail';
import LocationList from './views/contacts/LocationList';
import LocationDetail from './views/contacts/LocationDetail';
import PageAlert from 'components/alerts/PageAlert';
import PageNotFound from 'components/alerts/PageNotFound';
import JobList from './views/jobs/JobList';
import TicketList from './views/tickets/TicketList';
import TicketDetail from './views/tickets/TicketDetail';
import ContractList from './views/contracts/ContractList';
import ContractDetail from './views/contracts/ContractDetail';
import PersonalPreferencesPage from './views/settings/PersonalPreferencesPage';
import Dashboard from './views/dashboard/Dashboard';
import AdminDashboard from './views/dashboard/admin/AdminDashboard';
import AdminDashboardTv from './views/dashboard/admin/AdminDashboardTv';
import Scheduler from './views/scheduling/Scheduling';
import PdfLayoutPage from './views/settings/pdf/PdfLayoutPage';
import ClientList from './views/contacts/ClientList';
import SupplierList from './views/contacts/SupplierList';
import LeadList from './views/contacts/LeadList';
import OrganizationList from './views/contacts/OrganizationList';
import PersonList from './views/contacts/PersonList';
import HourlyRateList from './views/products/HourlyRateList';
import TrialWizard from './views/dashboard/TrialWizard';
import TrialHomePage from './views/dashboard/TrialHomePage';
import InvoiceList from './views/invoices/InvoiceList';
import InvoiceTemplateList from './views/invoices/InvoiceTemplateList';
import ContactDetail from './views/contacts/ContactDetail';
import ContactImport from './views/contacts/ContactImport';
import ContactMerge from './views/contacts/ContactMerge';
import AttachmentImport from './views/attachment/AttachmentImport';
import ProposalList from './views/proposals/ProposalList';
import OrderList from './views/order/OrderList';
import DealDetail from './views/deal/DealDetail';
import DealPage from './views/deal/DealPage';
import SubscriptionWizard from './views/settings/billing/subscription-wizard/SubscriptionWizard';
import ThankYouPage from './views/settings/billing/subscription-wizard/ThankYouPage';
import EmailTemplateLibrary from './views/settings/email/EmailTemplateLibraryPage';
import TaskList from './views/tasks/TaskList';
import EmailLayoutPage from './views/settings/email/EmailLayoutPage';
import KnowledgeBaseArticleEditorPage from './views/settings/knowledge_base/KnowledgeBaseArticleEditorPage';
import KnowledgeBasePage from './views/knowledge_base/KnowledgeBasePage';
import KnowledgeBaseGroupPage from './views/knowledge_base/KnowledgeBaseGroupPage';
import KnowledgeBaseArticlePage from './views/knowledge_base/KnowledgeBaseArticlePage';
import NewsletterOverviewPage from './views/contacts/newsletters/NewsletterOverviewPage';
import NewsletterEditorPage from './views/contacts/newsletters/NewsletterEditorPage';
import NewsletterReportPage from './views/contacts/newsletters/NewsletterReportPage';
import NewsletterAudiencePage from './views/contacts/newsletters/NewsletterAudiencePage';
import EmailLayoutLibrary from './views/settings/email/EmailLayoutLibrary';
import EmailList from './views/email/EmailList';
import SmsList from './views/sms/SmsList';
import SmsThankYouPage from './views/settings/sms/SmsThankYouPage';
import DayPassThankYouPage from './views/subcontractors/DayPassThankYouPage';
import SmsTemplateLibraryPage from './views/settings/sms/SmsTemplateLibraryPage';
import SmsTemplatePage from './views/settings/sms/SmsTemplatePage';
import TimeRowList from './views/timesheets/TimeRowList';
import EmailTemplatePage from './views/settings/email/EmailTemplatePage';
import ApiDocs from './views/docs/ApiDocs';
import LegacyImportWizard from './views/legacy_import/LegacyImportWizard';
import ProjectList from './views/projects/ProjectList';
import UserHourList from './views/timesheets/UserHourList';
import UserTimeRowList from './views/timesheets/UserTimeRowList';
import PurchaseList from './views/purchases/PurchaseList';
import OnboardingVideoPage from './views/dashboard/OnboardingVideoPage';
import ProductImport from './views/products/ProductImport';
import JobImport from './views/jobs/JobImport';
import PersonalCalendarScheduler from './views/scheduling/personal-calendar/PersonalCalenderOnly';
import AssetList from './views/assets/AssetList';
import AssetDetail from './views/assets/AssetDetail';
import FormBuilder from './views/forms/FormBuilder';
import FormTemplateBuilder from './views/forms/FormTemplateBuilder';
import EmployeeList from './views/employees/EmployeeList';
import EmployeeDetail from './views/employees/EmployeeDetail';

/*
 * Routes 
 * The purpose of the Routes file is to be the place to name links and 
 * paths to other pages, and store them in one place.
 * 
 * Use the PrivateRoute component for links that should only be opened
 * by logged in users with the right permissions.
 * 
 * Use the PublicRoute component for links that should only be opened
 * by users which are not logged in. For example, a login form or 
 * trial creation form.
 * 
 * The PrivateRoute and PublicRoute components are a proprietary 
 * function that extends the original Routes component.
 * 
 * Use the Route component for links that may be opened by users
 * which are, or which are not logged in.
 * 
 * Use 'exact' for main directories which contains other paths
 * below it. For example for '/products' where also is '/products/1201010/'
 * below. Otherwise the below paths doesn't render.
 */

const Routes: React.FC = () => {
    const { isLoggedIn, redirectTo } = useAuthContext();

    return (
        <Switch>
            {/* Redirect '/' to dashboard or login page */}
            <Route path="/" exact>
                {isLoggedIn === true && redirectTo 
                    ? <Redirect to={`/${redirectTo}`} /> 
                    : <Redirect to="/login" />
                }
            </Route>
            
            {/* Public routes for login or account creation pages */}
            <PublicRoute path="/login" exact component={LoginPage} />
            <PublicRoute path="/login/two-factor" component={TwoFactorPage} />
            <PublicRoute path="/password-forgotten" component={ForgotPasswordPage} />
            <PublicRoute path="/reset-password/:uid/:token" component={ResetPasswordPage} />
            <PublicRoute path="/set-password/:token" component={SetPasswordPage} />
            <PublicRoute path="/create-account" component={CreateTrialPage} />

            {/* Public payment page, a plain route so logged in users are not redirected away */}
            <Route path="/pay/:environmentHash" component={PayInvoicePage} />

            {/* Api Docs, a public route so the docs render without the navbar of the app */}
            <PublicRoute path="/api-docs/v3" component={ApiDocs} />

            {/* Public proposal portal for customers */}
            <PublicRoute path="/portal/proposal/:token" component={ProposalPortalPage} />

            {/* Public knowledge base, plain routes so logged in users can read the restricted articles */}
            <Route path="/kennisbank" exact component={KnowledgeBasePage} />
            <Route path="/kennisbank/groep/:id" component={KnowledgeBaseGroupPage} />
            <Route path="/kennisbank/artikel/:id" component={KnowledgeBaseArticlePage} />

            {/* Home */}
            <PrivateRoute path="/dashboard" component={Dashboard} />
            <PrivateRoute path="/tasks" component={TaskList} />
            <PrivateRoute path="/emails" component={EmailList} />
            <PrivateRoute path="/sms-messages" component={SmsList} />

            {/* Trial wizard. Prevent redirecting to the wizard if its already done */}
            <PrivateRoute
                path="/trial-wizard/:step"
                render={() =>
                    isLoggedIn && redirectTo === 'trial-wizard/1'
                        ? <TrialWizard />
                        : <Redirect to={isLoggedIn ? '/dashboard' : '/login'} />
                }
            />

            {/* Forced two factor setup. Only render while the setup is still required */}
            <PrivateRoute
                path="/setup-2fa"
                render={() =>
                    isLoggedIn && redirectTo === 'setup-2fa'
                        ? <TwoFactorSetupPage />
                        : <Redirect to={isLoggedIn ? '/dashboard' : '/login'} />
                }
            />
            
            <PrivateRoute path="/trial-homepage" component={TrialHomePage} />
            <PrivateRoute path="/onboarding-videos" component={OnboardingVideoPage} />
            <PrivateRoute path="/admin-dashboard" permissionCheck='no_user' component={AdminDashboard} exact />

            {/* Plain route: reachable without logging in, gated by its own viewer token */}
            <Route path="/admin-dashboard/tv" component={AdminDashboardTv} />

            {/* Sales */}
            <PrivateRoute path="/proposals" activeFeature='proposals' component={ProposalList} />
            <PrivateRoute path="/orders" component={OrderList} />
            <PrivateRoute path="/deals" component={DealPage} exact />
            <PrivateRoute path="/deals/:id" component={DealDetail} />

            {/* Scheduler */}
            <PrivateRoute path="/scheduling" activeFeature='scheduling' component={Scheduler} />
            <PrivateRoute path="/calendar" component={PersonalCalendarScheduler} />

            {/* Jobs */}
            <PrivateRoute path="/projects" component={ProjectList} />
            <PrivateRoute path="/jobs" component={JobList} />
            <PrivateRoute path="/tickets/list" activeFeature='tickets' component={TicketList} />
            <PrivateRoute path="/tickets/:id" activeFeature='tickets' component={TicketDetail} />
            <PrivateRoute path="/tickets" activeFeature='tickets' component={TicketDetail} />
            <PrivateRoute path="/user-hours" component={UserHourList} exact />
            <PrivateRoute path="/user-hours/timerows" component={UserTimeRowList} />
            <PrivateRoute path="/contracts" activeFeature='contract_management' component={ContractList} exact />
            <PrivateRoute path="/contracts/:id" activeFeature='contract_management' component={ContractDetail} />

            {/* Invoices */}
            <PrivateRoute path="/invoices/periodic" activeFeature='periodic_invoices' component={InvoiceTemplateList} />
            <PrivateRoute path="/invoices" component={InvoiceList} exact />

            {/* Team */}
            <PrivateRoute path="/timesheet" component={TimeRowList} />
            <PrivateRoute path="/employees" activeFeature='hrm' component={EmployeeList} exact />
            <PrivateRoute path="/employees/:id" activeFeature='hrm' component={EmployeeDetail} />
            <PrivateRoute path="/leave-requests" component={undefined} />
            <PrivateRoute path="/expenses" component={undefined} />

            {/* Products */}
            <PrivateRoute path="/products/" activeFeature='products' component={ProductList} exact />
            <PrivateRoute path="/products/:id" activeFeature='products' component={ProductDetail} />
            <PrivateRoute path="/hourly-rates/" activeFeature='products' component={HourlyRateList} exact />
            <PrivateRoute path="/services" activeFeature='products' component={ServiceList} exact />
            <PrivateRoute path="/inventory" component={undefined} />
            <PrivateRoute path="/stockrooms" component={undefined} />
            <PrivateRoute path="/purchases" activeFeature='purchases' component={PurchaseList} />
            <PrivateRoute path="/product_categories" activeFeature='product_categories' component={ProductCategoryList} />

            {/* Contacts */}
            <Route path="/contacts/" exact render={() => <Redirect to="/contacts/clients" />} />
            <PrivateRoute path="/contacts/clients/" activeFeature='contacts' component={ClientList} exact />
            <PrivateRoute path="/contacts/suppliers/" activeFeature='purchases' component={SupplierList} exact />
            <PrivateRoute path="/contacts/leads/" activeFeature='contacts' component={LeadList} exact />
            <PrivateRoute path="/contacts/organizations/" activeFeature='contacts' component={OrganizationList} exact />
            <PrivateRoute path="/contacts/persons/" activeFeature='contacts' component={PersonList} exact />
            <PrivateRoute path="/contacts/merge" activeFeature='contacts' component={ContactMerge} exact />
            <PrivateRoute path="/contacts/contact/:id" activeFeature='contacts' component={ContactDetail} />
            <PrivateRoute path="/contacts/organization/:id" activeFeature='contacts' component={OrganizationDetail} />
            <PrivateRoute path="/contacts/person/:id" activeFeature='contacts' component={PersonDetail} />
            <PrivateRoute path="/contacts/work-locations/" activeFeature='contacts' component={LocationList} exact />
            <PrivateRoute path="/contacts/work-locations/:id" activeFeature='contacts' component={LocationDetail} />
            <PrivateRoute path="/contacts/assets/" activeFeature='asset_management' component={AssetList} exact />
            <PrivateRoute path="/contacts/assets/:id" activeFeature='asset_management' component={AssetDetail} />
            <PrivateRoute path="/contacts/newsletters" exact activeFeature='newsletters' component={NewsletterOverviewPage} />
            <PrivateRoute path="/contacts/newsletters/overview" exact activeFeature='newsletters' component={NewsletterOverviewPage} />
            <PrivateRoute path="/contacts/newsletters/audiences" exact activeFeature='newsletters' component={NewsletterOverviewPage} />
            <PrivateRoute path="/contacts/newsletters/new" exact activeFeature='newsletters' component={NewsletterEditorPage} />
            <PrivateRoute path="/contacts/newsletters/audiences/new" exact activeFeature='newsletters' component={NewsletterAudiencePage} />
            <PrivateRoute path="/contacts/newsletters/audiences/:id(\d+)" activeFeature='newsletters' component={NewsletterAudiencePage} />
            <PrivateRoute path="/contacts/newsletters/:id(\d+)/report" activeFeature='newsletters' component={NewsletterReportPage} />
            <PrivateRoute path="/contacts/newsletters/:id(\d+)" activeFeature='newsletters' component={NewsletterEditorPage} />

            {/* Import data */}
            <PrivateRoute path="/import-data/contact/" component={ContactImport} />
            <PrivateRoute path="/import-data/product/" component={ProductImport} />
            <PrivateRoute path="/import-data/attachment/" component={AttachmentImport} />
            <PrivateRoute path="/import-data/job/" component={JobImport} />
            <PrivateRoute path="/legacy-migration/" permissionCheck='no_user' component={LegacyImportWizard} exact />

            {/* Redirect /settings to /company-details */}
            <PrivateRoute path="/settings" exact permissionCheck='change_settings'>
                <Redirect to="/settings/general/company-details" />
            </PrivateRoute>

            {/* Full page setting pages such as pdf layout, workflow customizer or subscription wizard */}
            <PrivateRoute path="/settings/templates/layout/pdf-layout/:id" permissionCheck='change_settings' component={PdfLayoutPage} />
            <PrivateRoute path="/settings/features/email/template-library" permissionCheck='change_settings' component={EmailTemplateLibrary} />
            <PrivateRoute path="/settings/features/email/template/new/:templateType" permissionCheck='change_settings' component={EmailTemplatePage} />
            <PrivateRoute path="/settings/features/email/template/:id" permissionCheck='change_settings' component={EmailTemplatePage} />
            <PrivateRoute path="/settings/features/email/layout-library" permissionCheck='change_settings' component={EmailLayoutLibrary} />
            <PrivateRoute path="/settings/features/email/layout/:id" permissionCheck='change_settings' component={EmailLayoutPage} />
            <PrivateRoute path="/settings/admin/knowledge-base/article/new/:groupId/:sectionId?" permissionCheck={['no_user', 'staff_user']} component={KnowledgeBaseArticleEditorPage} />
            <PrivateRoute path="/settings/admin/knowledge-base/article/:id" permissionCheck={['no_user', 'staff_user']} component={KnowledgeBaseArticleEditorPage} />
            <PrivateRoute path="/settings/features/sms/template-library" permissionCheck='change_settings' component={SmsTemplateLibraryPage} />
            <PrivateRoute path="/settings/features/sms/template/new/:templateType" permissionCheck='change_settings' component={SmsTemplatePage} />
            <PrivateRoute path="/settings/features/sms/template/:id" permissionCheck='change_settings' component={SmsTemplatePage} />

            <PrivateRoute path="/settings/custom-fields/form-builder/form/new" component={FormBuilder} />
            <PrivateRoute path="/settings/custom-fields/form-builder/form/:id" component={FormBuilder} />
            <PrivateRoute path="/settings/custom-fields/form-builder/template/new" component={FormTemplateBuilder} />
            <PrivateRoute path="/settings/custom-fields/form-builder/template/:id" component={FormTemplateBuilder} />

            <PrivateRoute path="/settings/billing/subscription/wizard" permissionCheck='change_settings' component={SubscriptionWizard} />
            <PrivateRoute path="/settings/billing/subscription/thank-you" permissionCheck='change_settings' component={ThankYouPage} />

            {/* Settings pages next to the settings submenu */}
            <PrivateRoute path="/settings" permissionCheck='change_settings'>
                <SettingsMenu>
                    <Switch>
                        <PrivateRoute path="/settings/admin/environments" permissionCheck={['no_user', 'staff_user']} component={SettingsPage} />
                        <PrivateRoute path="/settings/admin/plans" permissionCheck='no_user' component={SettingsPage} />
                        <PrivateRoute path="/settings/admin/releases" permissionCheck={['no_user', 'staff_user']} component={SettingsPage} />
                        <PrivateRoute path="/settings/admin/maintenance" permissionCheck='no_user' component={SettingsPage} />
                        <PrivateRoute path="/settings/admin/knowledge-base" permissionCheck={['no_user', 'staff_user']} component={SettingsPage} />
                        <PrivateRoute path="/settings/general/company-details" component={SettingsPage} />
                        <PrivateRoute path="/settings/general/divisions" activeFeature='divisions' component={SettingsPage} />                    
                        <PrivateRoute path="/settings/general/users" component={SettingsPage} />
                        <PrivateRoute path="/settings/general/subcontractors/thank-you" permissionCheck='change_settings' component={DayPassThankYouPage} />
                        <PrivateRoute path="/settings/general/subcontractors/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/general/security" activeFeature='enhanced_security' component={SettingsPage} />
                        <PrivateRoute path="/settings/features/scheduling" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/jobs/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/tickets/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/contracts/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/timesheets" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/projects" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/mobile-app" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/deals" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/proposals" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/orders" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/purchases" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/invoices" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/email/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/sms/thank-you" permissionCheck='change_settings' component={SmsThankYouPage} />
                        <PrivateRoute path="/settings/features/sms/:tab?" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/contacts" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/assets" component={SettingsPage} />
                        <PrivateRoute path="/settings/features/reporting" activeFeature='reporting' component={SettingsPage} />
                        <PrivateRoute path="/settings/features/additional-modules" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/labels" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/categories" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/workflows" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/data-imports" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/fields" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/forms" component={SettingsPage} />
                        <PrivateRoute path="/settings/custom-fields/form_templates" component={SettingsPage} />
                        <PrivateRoute path="/settings/templates/email-sms-templates" component={SettingsPage} />
                        <PrivateRoute path="/settings/templates/layouts" component={SettingsPage} />
                        <PrivateRoute path="/settings/integrations/calendar" component={SettingsPage} />
                        <PrivateRoute path="/settings/integrations/email-integration" component={SettingsPage} />
                        <PrivateRoute path="/settings/integrations/integrations-library" component={SettingsPage} />
                        <PrivateRoute path="/settings/integrations/api-keys" component={SettingsPage} />
                        <PrivateRoute path="/settings/billing/subscription" component={SettingsPage} />
                        <PrivateRoute path="/settings/billing/invoices" component={SettingsPage} />
                    </Switch>
                </SettingsMenu>
            </PrivateRoute>

            {/* Personal preferences */}
            <PrivateRoute path="/preferences" component={PersonalPreferencesPage} />

            {/* Redirect to access denied page */}
            <PrivateRoute path="/access-denied">
                <PageAlert 
                    title='alert.access_denied_label' 
                    type='danger'
                    message='alert.access_denied_message' 
                />
            </PrivateRoute>

            {/* Redirect not existing pages to the login page or show 404 page alert */}
            <Route path="*"><PageNotFound /></Route>
        </Switch>
    );
};

export default Routes;